import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Clock,
  Calendar,
  User,
  Flag,
  Tag,
  History,
  ListTree,
  Workflow,
} from 'lucide-react'
import { useState } from 'react'

import { InlineStatusSelect } from '@/components/common/inline-status-select'
import { IssueForm } from '@/components/pm/issue/issue-form'
import { UnifiedHistory } from '@/components/pm/issue/unified-history'
import { QuickTimelogButton } from '@/components/pm/timelog/quick-timelog-button'
import { WorkflowPanel } from '@/components/pm/workflow'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/components/ui/toast'
import { useAuth, useEmployeesByIds, useTeam } from '@/hooks'
import {
  useIssue,
  useDeleteIssue,
  useIssueHistory,
  useIssueChildren,
  useUpdateIssueStatus,
  useWorkflowHistory,
  type IssueStatus,
  type IssueType,
  type IssuePriority,
  useProject,
  type DeveloperGroup,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/pm/issues/$issueId')({
  component: IssueDetailPage,
})

const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: '待办',
  todo: '计划中',
  in_progress: '进行中',
  code_review: '代码审查',
  testing: '测试中',
  done: '已完成',
  blocked: '已阻塞',
  cancelled: '已取消',
}

const TYPE_LABELS: Record<IssueType, string> = {
  epic: '史诗',
  story: '故事',
  task: '任务',
  bug: '缺陷',
}

const TYPE_COLORS: Record<IssueType, string> = {
  epic: 'bg-purple-100 text-purple-800',
  story: 'bg-green-100 text-green-800',
  task: 'bg-blue-100 text-blue-800',
  bug: 'bg-red-100 text-red-800',
}

const PRIORITY_LABELS: Record<IssuePriority, string> = {
  urgent: '紧急',
  high: '高',
  medium: '中',
  low: '低',
}

const PRIORITY_COLORS: Record<IssuePriority, string> = {
  urgent: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-gray-600',
}

const DEVELOPER_GROUP_LABELS: Record<DeveloperGroup, string> = {
  product: '产品',
  design: '设计',
  frontend: '前端',
  backend: '后端',
  testing: '测试',
}

function IssueDetailPage() {
  const params = Route.useParams()
  const issueId = params.issueId
  const navigate = useNavigate()
  const toast = useToast()
  const [showEditForm, setShowEditForm] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'children' | 'workflow'>(
    'details'
  )

  const { data: issue, isLoading, error } = useIssue(issueId)
  const { data: project } = useProject(issue?.projectId ?? '')
  const { data: team } = useTeam(project?.teamId ?? '')
  const { data: issueHistory, isLoading: isIssueHistoryLoading } = useIssueHistory(issueId)
  const { data: workflowHistory, isLoading: isWorkflowHistoryLoading } = useWorkflowHistory(issueId)
  const { data: children } = useIssueChildren(issueId)

  // 合并历史记录总数
  const totalHistoryCount = (issueHistory?.length ?? 0) + (workflowHistory?.length ?? 0)
  const { getEmployeeName } = useEmployeesByIds([issue?.assigneeId, issue?.reporterId])
  const { user } = useAuth()
  const deleteMutation = useDeleteIssue()
  const updateStatusMutation = useUpdateIssueStatus()

  // 检查当前用户是否是团队组长或有权限修改阶段审核人
  const isTeamLeader =
    team?.leaderId === user?.id || user?.role === 'director' || user?.role === 'pm'

  const handleDelete = () => {
    deleteMutation.mutate(issueId, {
      onSuccess: () => {
        toast.success('任务已删除')
        void navigate({ to: '/pm/issues' })
      },
      onError: (err) => {
        toast.error('删除失败', err.message)
      },
    })
  }

  const handleStatusChange = async (newStatus: IssueStatus) => {
    if (!issue) {
      return
    }
    await updateStatusMutation.mutateAsync({
      id: issue.id,
      data: { status: newStatus },
    })
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (error || !issue) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">{error?.message ?? '任务不存在'}</div>
        <Link to="/pm/issues" className="text-primary hover:underline">
          返回任务列表
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 返回和操作 */}
      <div className="flex items-center justify-between">
        <Link
          to="/pm/issues"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          返回任务列表
        </Link>
        <div className="flex items-center gap-2">
          <QuickTimelogButton
            issueId={issue.id}
            issueName={issue.title}
            projectId={issue.projectId}
          />
          <button
            onClick={() => setShowEditForm(true)}
            className="hover:bg-muted inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
          >
            <Edit className="h-4 w-4" />
            编辑
          </button>
          <button
            onClick={() => setShowDeleteDialog(true)}
            disabled={deleteMutation.isPending}
            className="text-destructive hover:bg-destructive/10 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
          >
            <Trash2 className="h-4 w-4" />
            删除
          </button>
        </div>
      </div>

      {/* 编辑表单 */}
      {showEditForm && (
        <IssueForm
          projectId={issue.projectId}
          issue={issue}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => setShowEditForm(false)}
        />
      )}

      {/* 删除确认对话框 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除任务 &quot;{issue.code}: {issue.title}&quot; 吗？此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 任务信息卡片 */}
      <div className="bg-card rounded-lg border">
        <div className="border-b px-6 py-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <span className="text-muted-foreground font-mono text-sm">{issue.code}</span>
                <span
                  className={cn(
                    'rounded-full px-2.5 py-0.5 text-xs font-medium',
                    TYPE_COLORS[issue.type]
                  )}
                >
                  {TYPE_LABELS[issue.type]}
                </span>
              </div>
              <h1 className="text-2xl font-bold">{issue.title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <InlineStatusSelect
                value={issue.status}
                onChange={handleStatusChange}
                onSaveSuccess={() => toast.success('状态已更新')}
                onSaveError={(err) => toast.error('更新失败', err.message)}
              />
            </div>
          </div>
        </div>

        {/* 标签页切换 */}
        <div className="flex gap-2 border-b px-6">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
              activeTab === 'details'
                ? 'border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            )}
          >
            详情
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
              activeTab === 'history'
                ? 'border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            )}
          >
            <History className="mr-1 inline h-4 w-4" />
            历史记录 {totalHistoryCount > 0 && `(${totalHistoryCount})`}
          </button>
          <button
            onClick={() => setActiveTab('children')}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
              activeTab === 'children'
                ? 'border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            )}
          >
            <ListTree className="mr-1 inline h-4 w-4" />
            子任务 {children && children.length > 0 && `(${children.length})`}
          </button>
          <button
            onClick={() => setActiveTab('workflow')}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
              activeTab === 'workflow'
                ? 'border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            )}
          >
            <Workflow className="mr-1 inline h-4 w-4" />
            工作流
          </button>
        </div>

        {/* 内容区域 */}
        <div className="min-w-0 p-6">
          {activeTab === 'details' && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* 描述 */}
              <div className="space-y-2 md:col-span-2">
                <h3 className="font-semibold">描述</h3>
                <div className="bg-muted/50 rounded-md p-4 text-sm">
                  {issue.description || <span className="text-muted-foreground">暂无描述</span>}
                </div>
              </div>

              {/* 基本信息 */}
              <div className="space-y-4">
                <h3 className="font-semibold">基本信息</h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Flag className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">优先级</dt>
                    <dd className={cn('font-medium', PRIORITY_COLORS[issue.priority])}>
                      {PRIORITY_LABELS[issue.priority]}
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">负责人</dt>
                    <dd>
                      {issue.assigneeId ? (
                        getEmployeeName(issue.assigneeId)
                      ) : (
                        <span className="text-muted-foreground">未分配</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">报告人</dt>
                    <dd>{getEmployeeName(issue.reporterId)}</dd>
                  </div>
                  {issue.developerGroup && (
                    <div className="flex items-center gap-2">
                      <Tag className="text-muted-foreground h-4 w-4" />
                      <dt className="text-muted-foreground w-20">开发组</dt>
                      <dd>{DEVELOPER_GROUP_LABELS[issue.developerGroup]}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* 时间信息 */}
              <div className="space-y-4">
                <h3 className="font-semibold">时间信息</h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">截止日期</dt>
                    <dd>
                      {issue.dueDate ? (
                        new Date(issue.dueDate).toLocaleDateString('zh-CN')
                      ) : (
                        <span className="text-muted-foreground">未设置</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">预估工时</dt>
                    <dd>
                      {issue.estimatedHours ? (
                        `${issue.estimatedHours} 小时`
                      ) : (
                        <span className="text-muted-foreground">未设置</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">实际工时</dt>
                    <dd>
                      {issue.actualHours ? (
                        `${issue.actualHours} 小时`
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="text-muted-foreground h-4 w-4" />
                    <dt className="text-muted-foreground w-20">创建时间</dt>
                    <dd>{new Date(issue.createdAt).toLocaleString('zh-CN')}</dd>
                  </div>
                  {issue.completedAt && (
                    <div className="flex items-center gap-2">
                      <Calendar className="text-muted-foreground h-4 w-4" />
                      <dt className="text-muted-foreground w-20">完成时间</dt>
                      <dd>{new Date(issue.completedAt).toLocaleString('zh-CN')}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* 阻塞原因 */}
              {issue.status === 'blocked' && issue.blockedReason && (
                <div className="space-y-2 md:col-span-2">
                  <h3 className="text-destructive font-semibold">阻塞原因</h3>
                  <div className="bg-destructive/10 text-destructive rounded-md p-4 text-sm">
                    {issue.blockedReason}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <UnifiedHistory
              issueHistory={issueHistory}
              workflowHistory={workflowHistory}
              isLoading={isIssueHistoryLoading || isWorkflowHistoryLoading}
              maxVisible={20}
            />
          )}

          {activeTab === 'children' && (
            <div className="space-y-4">
              {children && children.length > 0 ? (
                <div className="divide-y rounded-lg border">
                  {children.map((child) => (
                    <Link
                      key={child.id}
                      to="/pm/issues/$issueId"
                      params={{ issueId: child.id }}
                      className="hover:bg-muted/50 flex items-center gap-4 p-4"
                    >
                      <span className="text-muted-foreground font-mono text-sm">{child.code}</span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          TYPE_COLORS[child.type]
                        )}
                      >
                        {TYPE_LABELS[child.type]}
                      </span>
                      <span className="flex-1 font-medium">{child.title}</span>
                      <span className="text-muted-foreground text-sm">
                        {STATUS_LABELS[child.status]}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground py-8 text-center">暂无子任务</div>
              )}
            </div>
          )}

          {activeTab === 'workflow' && (
            <WorkflowPanel
              issueId={issue.id}
              issueTitle={issue.title}
              teamId={project?.teamId}
              isTeamLeader={isTeamLeader}
            />
          )}
        </div>
      </div>
    </div>
  )
}

